import * as d3 from "d3";
import type { PriceDaily } from "epsm-api-types/models";
import { type RefObject, useEffect } from "react";
import { formatValuePercent } from "@/components/widgets/helpers/FormattingHelpers";
import { SECTORS, TIME_RANGE_MAP } from "../../../config";
import type { SectorsChartType, SectorsTimeRange, Stock } from "../../../types";
import { getPreparedStocks } from "../../helpers/getPreparedStocks/getPreparedStocks";
import { updateChart } from "../../helpers/updateChart";

type Args = {
	svgRef: RefObject<SVGSVGElement | null>;
	stocks: PriceDaily[];
	timeFormat: SectorsTimeRange;
	parentWidth: number | undefined;
	chartType: SectorsChartType;
};

export function useCreateSectorsChart(args: Args) {
	const { svgRef, stocks, timeFormat, parentWidth = 500, chartType } = args;

	const step = TIME_RANGE_MAP[timeFormat].xAxisStep;

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		if (!svgRef.current || !stocks.length) return;

		const stocksPrepared = getPreparedStocks({
			chartType,
			stocks,
			timeFormat,
		});

		if (!stocksPrepared) return;

		// Specify the chart's dimensions.
		const width = parentWidth;
		const height = 600;
		const marginTop = 20;
		const marginRight = 90;
		const marginBottom = 30;
		const marginLeft = 60;
		const fontSize = 14;
		const axisFont = `${fontSize}px sans-serif`;

		const series = d3
			.groups(stocksPrepared, (d) => d.Symbol)
			.map(([key, values]) => {
				const v = values[0].Close ?? 1; // Default to 1 if undefined
				return {
					key,
					values: values.map(({ Date: date, Close }) => ({
						Date: date,
						value: (Close ?? 0) / v,
					})),
					visible: true, // Add visibility state for toggling
				};
			});

		const k =
			d3.max(series, ({ values }) => {
				const maxVal = d3.max(values, (d) => d.value) ?? 1;
				const minVal = d3.min(values, (d) => d.value) ?? 1;
				return maxVal / minVal;
			}) ?? 1;

		const y = d3
			.scaleLog()
			.domain([1 / k, k])
			.rangeRound([height - marginBottom, marginTop]);

		const z = d3
			.scaleOrdinal(d3.schemeCategory10)
			.domain(series.map((d) => d.key));

		const bisect = d3.bisector((d: Stock) => d.Date).left;

		// Clear previous contents
		d3.select(svgRef.current).selectAll("*").remove();

		// Setup SVG
		const svg = d3
			.select(svgRef.current)
			.attr("width", "100%")
			.attr("height", height)
			.attr("viewBox", [0, 0, width, height])
			.attr("style", "max-width: 100%; height: auto; color: #c3c3c3;");

		// Create axes X
		// Get unique dates from the data for point scale
		const uniqueDates = Array.from(
			new Set(stocksPrepared.map((d) => d.Date)),
		).sort((a, b) => a.getTime() - b.getTime()) as Iterable<string>;

		// Replace the continuous scale with a point scale
		const x = d3
			.scalePoint()
			.domain(uniqueDates)
			.range([marginLeft, width - marginRight])
			.padding(0.2);

		// Create X axis with the point scale
		svg
			.append("g")
			.attr("transform", `translate(0,${height - marginBottom})`)
			.style("font", axisFont)
			.call((xAxis) => {
				// Create the axis
				const axis = d3
					.axisBottom(x as unknown as d3.AxisScale<string>)
					.tickSizeOuter(0);

				// Apply the axis to the selection
				xAxis.call(axis);

				// Only show a subset of tick labels (e.g., every 5th tick)
				xAxis
					.selectAll(".tick text")
					.style("display", (_, i) => (i % step === 0 ? "inline" : "none"))
					.text((d) => (d instanceof Date ? d3.timeFormat("%b %d")(d) : ""));

				return xAxis;
			});

		// Create axes Y
		svg
			.append("g")
			.attr("transform", `translate(${marginLeft},0)`)
			.style("font", axisFont)
			.call(
				d3
					.axisLeft(y)
					.tickFormat((d) => formatValuePercent(d)) // Cleaner percentage formatting
					.tickSizeOuter(0),
			);

		// Add rule cross-line
		const rule = svg
			.append("g")
			.append("line")
			.attr("y1", height)
			.attr("y2", 0)
			.attr("stroke", "lightgrey");

		// Create lines and labels
		const seriesGroup = svg
			.append("g")
			.style("font", `bold ${fontSize}px sans-serif`)
			.selectAll("g")
			.data(series)
			.join("g");

		const line = d3
			.line<{ Date: string | Date; value: number }>()
			.x((d) => x(d.Date as unknown as string) || 0) // Add fallback value of 0 for undefined
			.y((d) => y(d.value));

		seriesGroup
			.append("path")
			.attr("class", "series-line")
			.attr("fill", "none")
			.attr("stroke-width", 1)
			.attr("stroke-linejoin", "round")
			.attr("stroke-linecap", "round")
			.attr(
				"stroke",
				(d: {
					key: string;
					values: { Date: Date; value: number }[];
					visible: boolean;
				}) => {
					return d.key in SECTORS
						? SECTORS[d.key as keyof typeof SECTORS].color
						: z(d.key);
				},
			)
			.attr("d", (d) => line(d.values));

		// Add legend
		const legendEntryWidth = 60;

		const legend = svg
			.append("g")
			.attr("class", "legend")
			.attr(
				"transform",
				`translate(${marginLeft}, ${marginTop - 15})`, // Positioned at the top
			);

		const legendEntries = legend
			.selectAll(".legend-entry")
			.data(series)
			.join("g")
			.attr("class", "legend-entry")
			.attr("transform", (_, i) => `translate(${i * legendEntryWidth + 50}, 0)`) // Horizontal arrangement
			.style("cursor", "pointer")
			.on("click", function (_, d) {
				// Toggle visibility
				d.visible = !d.visible;

				// Update opacity of corresponding line
				seriesGroup
					.filter((s) => s.key === d.key)
					.select(".series-line")
					.transition()
					.duration(200)
					.attr("opacity", d.visible ? 1 : 0);

				// Update opacity of corresponding labels next to the chart lines
				seriesGroup
					.filter((s) => s.key === d.key)
					.selectAll("text")
					.transition()
					.duration(200)
					.attr("opacity", d.visible ? 1 : 0);

				// Update legend item appearance
				d3.select(this)
					.select("text")
					.style("opacity", d.visible ? 1 : 0.3);

				d3.select(this)
					.select("rect")
					.style("opacity", d.visible ? 1 : 0.3);
			});

		// Add colored rectangle for each legend entry
		legendEntries
			.append("rect")
			.attr("width", 12)
			.attr("height", 12)
			.attr("fill", (d) => {
				return d.key in SECTORS
					? SECTORS[d.key as keyof typeof SECTORS].color
					: z(d.key);
			});

		// Add text label for each legend entry
		legendEntries
			.append("text")
			.attr("x", 16)
			.attr("y", 10)
			.text((d) => {
				return d.key in SECTORS
					? SECTORS[d.key as keyof typeof SECTORS].ticker || d.key
					: d.key;
			})
			.style("font-size", `${fontSize}px`) // Smaller font for better horizontal fit
			.attr("fill", (d) => {
				return d.key in SECTORS
					? SECTORS[d.key as keyof typeof SECTORS].color
					: z(d.key);
			});

		// Helper function to find closest date in domain to mouse position
		const findClosestDate = (mouseX: number) => {
			// Create a map of x positions to dates for better performance
			const xToDateMap = new Map<number, string>();
			const domain = x.domain();

			for (const date of domain) {
				const xPos = x(date) || 0;
				xToDateMap.set(Math.round(xPos), date);
			}

			// Find closest x position to mouse position
			const roundedMouseX = Math.round(mouseX);
			let closestXPos = domain.length > 0 ? x(domain[0]) || 0 : 0;
			let minDistance = Math.abs(closestXPos - roundedMouseX);

			// Using the map for faster lookups
			for (const [xPos, _] of xToDateMap.entries()) {
				const distance = Math.abs(xPos - roundedMouseX);
				if (distance < minDistance) {
					minDistance = distance;
					closestXPos = xPos;
				}
			}

			// Get the date from the map
			const closestDate = xToDateMap.get(closestXPos) || domain[0];
			return new Date(closestDate);
		};

		// Add event listener
		svg.on("mousemove touchmove", function (event) {
			const mouseX = d3.pointer(event, this)[0];
			enhancedUpdateChart({
				date: findClosestDate(mouseX),
				d3,
				bisect,
				x,
				y,
				z,
				rule,
				seriesGroup,
			});
		});

		// Initial animation
		d3.transition()
			.ease(d3.easeCubicOut)
			.duration(1500)
			.tween("date", () => {
				const domain = x.domain();
				const startDate = new Date(domain[domain.length - 1]);
				const endDate = new Date(domain[0]);
				const i = d3.interpolateDate(startDate, endDate);

				return (t) =>
					enhancedUpdateChart({
						date: i(t),
						d3,
						bisect,
						x,
						y,
						z,
						rule,
						seriesGroup,
					});
			});

		// Add a tooltip for date display
		const tooltip = svg
			.append("g")
			.attr("class", "tooltip")
			.style("opacity", 0);

		tooltip
			.append("rect")
			.attr("width", TIME_RANGE_MAP[timeFormat].tooltipWidth)
			.attr("height", 28)
			.attr("fill", "#333")
			.attr("rx", 6)
			.attr("ry", 6);

		const tooltipText = tooltip
			.append("text")
			.attr("x", 16)
			.attr("y", 20)
			.attr("fill", "white")
			.style("font-size", `${fontSize}px`);

		// Create an enhanced version of updateChart that includes tooltip
		const enhancedUpdateChart = (params: Parameters<typeof updateChart>[0]) => {
			updateChart(params);

			// Update tooltip with date
			// const dateFormatter = d3.timeFormat("%m/%d/%y");
			const dateFormatter = d3.timeFormat(
				TIME_RANGE_MAP[timeFormat].dataFormat,
			);
			tooltipText.text(dateFormatter(params.date));

			// Position tooltip above the rule line
			tooltip
				.attr(
					"transform",
					`translate(${Math.min(Math.max((params.x(params.date as unknown as string) ?? 0) - 50, marginLeft), width - marginRight - 50)}, ${height / 2})`,
				)
				.style("opacity", 1);

			// Make sure the rule line is visible
			rule.style("opacity", 1);
		};

		// Hide tooltip when mouse leaves
		svg.on("mouseleave", () => {
			tooltip.style("opacity", 0);
		});
	}, [stocks.length, svgRef, timeFormat, parentWidth, step, chartType]);
}
